import { useLocation } from 'react-router-dom';
import { companyPagesMenu, talentPagesMenu } from '../menu';

type MenuLike = Record<string, any>;

/** Every route a section owns, paired with the section's hue. Longest path wins the match. */
const collectPaths = (section: MenuLike, accent: string, into: [string, string][]) => {
	// Any `*Path` key is a route this section owns: `path`, but also `detailPath`, `editPath`
	// and the rest, so a detail page keeps its section's colour instead of falling back.
	Object.entries(section).forEach(([key, value]) => {
		if (typeof value === 'string' && (key === 'path' || key.endsWith('Path'))) {
			into.push([`/${value.replace(/^\//, '')}`, accent]);
		}
	});
	if (section.subMenu && typeof section.subMenu === 'object') {
		Object.values(section.subMenu as MenuLike).forEach((child) =>
			collectPaths(child as MenuLike, accent, into),
		);
	}
};

const buildIndex = (): [string, string][] => {
	const entries: [string, string][] = [];
	[companyPagesMenu, talentPagesMenu].forEach((menu) => {
		Object.values(menu as MenuLike).forEach((section) => {
			const accent = String((section as MenuLike)?.iconColorClass ?? '').replace(
				'nav-c-',
				'',
			);
			if (!accent) return;
			collectPaths(section as MenuLike, accent, entries);
		});
	});
	// A route with a parameter (`/company/openings/:openingId`) is matched by its literal prefix,
	// which the parent `path` already provides — so drop the parameterised duplicates and keep the
	// index to plain prefixes.
	return entries
		.filter(([path]) => !path.includes(':'))
		.sort((a, b) => b[0].length - a[0].length);
};

const PATH_ACCENTS = buildIndex();

/**
 * The area accent of the page currently open, as the class that sets `--ot-accent` — or an empty
 * string outside the two flows.
 *
 * The six hues in `$ot-nav-accents` were assigned per menu section and then never left the sidebar:
 * the page you actually landed on stayed grey. This reads the accent back out of the same menu
 * definition, so the colour of the row you clicked and the colour of the page you are standing on
 * can never disagree — which is what makes it read as "where you are" rather than as decoration
 * (BR-UI-33, BR-UI-36).
 *
 * @userstory US-UI-42
 */
const useAreaAccent = (): string => {
	const { pathname } = useLocation();
	const match = PATH_ACCENTS.find(
		([path]) => pathname === path || pathname.startsWith(`${path}/`),
	);
	return match ? `ot-area-${match[1]}` : '';
};

export default useAreaAccent;
